import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";

const ProductCard = ({ p }) => {
  const imgs = p.images || [];
  return (
    <Link to={`/le-giraffe-consigliano/${p.id}`} data-testid={`rec-product-${p.id}`} className="group flex flex-col">
      <div className="overflow-hidden mb-4 bg-white border border-[#E7E5E4] aspect-[3/2] flex items-center justify-center p-4">
        {imgs.length > 0 ? (
          <img src={mediaUrl(imgs[0])} alt={p.title} loading="lazy" className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-[1.05]" />
        ) : (
          <span className="font-display italic text-[#A8A29E]">Nessuna immagine</span>
        )}
      </div>
      <h3 className="font-display text-2xl leading-tight tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors">
        {p.title}
      </h3>
      <span className="inline-flex items-center gap-2 mt-3 label-ui text-[#9F583F]">
        Leggi il consiglio <ArrowRight size={14} strokeWidth={1.6} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
};

export default function SubcategoryProducts() {
  const { subId } = useParams();
  const [sub, setSub] = useState(null);
  const [cat, setCat] = useState(null);
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      api.get("/rec/subcategories"),
      api.get("/rec/categories"),
      api.get("/rec/products"),
    ])
      .then(([sRes, cRes, pRes]) => {
        const s = sRes.data.find((x) => x.id === subId) || null;
        setSub(s);
        setCat(s ? cRes.data.find((c) => c.id === s.category_id) || null : null);
        setProducts(pRes.data.filter((p) => p.subcategory_id === subId));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [subId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products;
    return products.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(term) ||
        (p.description || "").toLowerCase().includes(term)
    );
  }, [products, q]);

  if (loaded && !sub)
    return (
      <div className="relative z-10">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <h1 className="font-display text-5xl text-[#1C1917] italic">Sottocategoria non trovata</h1>
          <Link to="/le-giraffe-consigliano" className="inline-block mt-8 label-ui text-[#9F583F] link-underline">
            Torna ai consigli
          </Link>
        </div>
        <Footer />
      </div>
    );

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <Header />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-8">
        <Link
          to="/le-giraffe-consigliano"
          className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors mb-8"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Le Giraffe Consigliano
        </Link>

        {cat && <p className="label-ui text-[#9F583F] mb-3">{cat.name}</p>}
        <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#1C1917]">
          {sub?.name || "…"}
        </h1>

        {/* Search bar */}
        <div className="mt-8 max-w-xl relative">
          <Search size={18} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <input
            data-testid="rec-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca un prodotto…"
            className="w-full font-body text-lg bg-white border border-[#E7E5E4] pl-12 pr-4 py-3.5 focus:outline-none focus:border-[#9F583F] transition-colors"
          />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        {filtered.length === 0 ? (
          <div className="border border-dashed border-[#E7E5E4] py-20 text-center">
            <p className="font-display text-2xl italic text-[#57534E]">
              {products.length === 0 ? "Presto qui troverai i primi consigli." : "Nessun prodotto corrisponde alla ricerca."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-testid="rec-sub-products-grid">
            {filtered.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
